import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import UIActions from '../../../ui/UIActions';
import addTestId from '../../../utils/addTestId';
import { useQuill } from '../../QuillContext';
import type QuillEditor from '../../QuillEditor';
import { getRangeSaver } from '../../RangeSaver';
import {
  checkmarkIcon,
  copyIcon,
  editLinkIcon,
  unlinkIcon,
} from '../editorIcons';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import styles from './LinkToolbar.module.scss';

interface LinkToolbarProps {
  quill: QuillEditor;
  onLinkChange: (value: string | null) => void;
}

const LinkToolbar = ({ onLinkChange, quill }: LinkToolbarProps) => {
  const dispatch = useDispatch();
  const {
    selection: { formats },
    editingLink: isEditingLink,
    editingLinkChange,
  } = useQuill();
  const [link, setLink] = useState(formats.link);

  useEffect(() => {
    setLink(formats.link);
  }, [formats.link]);

  const handleSave = (value: string | null) => {
    const range = getRangeSaver(quill).getLast();

    quill.focusWithoutScrolling();
    if (range.length) {
      quill.setSelection(range);
    } else {
      // Expand the range to cover the entire link if the selection is collapsed
      const [leaf, offset] = quill.getLeaf(range.index);
      if (leaf) {
        quill.setSelection(range.index - offset, leaf.length());
      }
    }

    onLinkChange(value);
    editingLinkChange(false);
  };

  const firstLink = Array.isArray(link) ? link[0] : link;
  return (
    <div className={styles.container}>
      {isEditingLink ? (
        <>
          <input
            autoFocus
            className={styles.input}
            type="url"
            value={firstLink || ''}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Paste a link"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSave(firstLink);
                e.preventDefault();
              } else if (e.key === 'Escape') {
                quill.focus();
                e.preventDefault();
              }
            }}
          />
          <div className={styles.saveButton}>
            <ToolbarButtonGroup>
              <ToolbarButton
                tooltip="Save"
                icon={checkmarkIcon}
                onClick={() => handleSave(firstLink)}
              />
            </ToolbarButtonGroup>
          </div>
        </>
      ) : (
        <>
          <a
            className={styles.link}
            rel="noopener noreferrer"
            target="_blank"
            href={firstLink}
            {...addTestId('linkToolbarLinkField')}
          >
            {firstLink}
          </a>
          <ToolbarButtonGroup>
            <ToolbarButton
              tooltip="Copy"
              icon={copyIcon}
              onClick={async () => {
                await navigator.clipboard.writeText(firstLink);
                dispatch(
                  UIActions.pushToast({
                    level: 'success',
                    message: 'Link copied',
                  }),
                );
                quill.focus();
              }}
            />
            <ToolbarButton
              icon={editLinkIcon}
              tooltip="Edit"
              active={isEditingLink}
              onClick={() => editingLinkChange(true)}
            />
            <ToolbarButton
              icon={unlinkIcon}
              tooltip="Remove"
              onClick={() => handleSave(null)}
            />
          </ToolbarButtonGroup>
        </>
      )}
    </div>
  );
};

export default LinkToolbar;
